/**
 * Status-bucket mapping for FS ticket statuses on the Application
 * Support dashboard.
 *
 * Six buckets, defined by Brett, that match how PMO/BA leadership
 * actually narrate the queue:
 *
 *   open             — Open
 *   in_progress      — In Progress
 *   pending_approval — Pending, Pending Approval, Awaiting Approval
 *   waiting          — Waiting, Waiting on Customer, Waiting on 3rd Party
 *   escalated        — Escalated, Jira, Sharepoint, Enhancement
 *   on_hold          — On Hold, Postponed, Scheduled (defaulted here —
 *                       confirm with Brett)
 *
 * Substring matching, not exact, so naming variants ('Waiting on
 * Customer' / 'Waiting on 3rd Party' / 'Pending Approval') collapse
 * naturally into the right bucket.
 */

export type HealthBucket =
  | "open"
  | "in_progress"
  | "pending_approval"
  | "waiting"
  | "escalated"
  | "on_hold"
  | "other";

export interface BucketMeta {
  label: string;
  /** Tailwind background for the stacked bar segment. */
  fill: string;
  /** Tailwind text color for the legend chip. */
  text: string;
  /** Tailwind background for the legend dot. */
  dot: string;
  /** Raw hex — used for SVG donut arcs (Tailwind classes don't apply
   *  to inline SVG attributes). Keep matched to the `fill` class. */
  hex: string;
}

export const BUCKET_META: Record<HealthBucket, BucketMeta> = {
  open: {
    label: "Open",
    fill: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-500",
    hex: "#10b981",
  },
  in_progress: {
    label: "In Progress",
    fill: "bg-sky-500",
    text: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-500",
    hex: "#0ea5e9",
  },
  pending_approval: {
    label: "Pending Approval",
    fill: "bg-brand-navy-500",
    text: "text-brand-navy-700 dark:text-brand-navy-300",
    dot: "bg-brand-navy-500",
    hex: "#6366f1",
  },
  waiting: {
    label: "Waiting",
    fill: "bg-amber-400",
    text: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-400",
    hex: "#fbbf24",
  },
  escalated: {
    label: "Escalated",
    fill: "bg-rose-500",
    text: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500",
    hex: "#f43f5e",
  },
  on_hold: {
    label: "On Hold",
    fill: "bg-slate-400",
    text: "text-ink-3",
    dot: "bg-slate-400",
    hex: "#94a3b8",
  },
  other: {
    label: "Other",
    fill: "bg-muted",
    text: "text-ink-muted",
    dot: "bg-muted-strong",
    hex: "#cbd5e1",
  },
};

export const BUCKET_ORDER: HealthBucket[] = [
  "open",
  "in_progress",
  "pending_approval",
  "waiting",
  "escalated",
  "on_hold",
  "other",
];

/**
 * Map a raw FS status_label to a health bucket. Order matters — we
 * test the more specific patterns (e.g. "pending approval") before the
 * looser ones ("pending"). Substring matching so "Waiting on Customer"
 * and "Waiting on 3rd Party" both collapse to "waiting" without us
 * having to maintain an exhaustive list.
 */
export function bucketFor(label: string): HealthBucket {
  const l = (label || "").toLowerCase().trim();
  if (!l) return "other";

  // Approval-flavored — anything mentioning approval or "pending" goes
  // here. Done early so "pending approval" doesn't get misrouted.
  if (l.includes("approv") || l.includes("pending")) return "pending_approval";

  // Stuck-on-someone-else — waiting on customer / 3rd party / etc.
  if (l.includes("waiting")) return "waiting";

  // Sent up the chain or out to another system.
  if (
    l.includes("escalat") ||
    l.includes("jira") ||
    l.includes("sharepoint") ||
    l.includes("enhanc")
  ) return "escalated";

  // Paused / parked. Scheduled defaults here for now (confirm with PMO).
  if (l.includes("on hold") || l.includes("postpon") || l.includes("schedul")) return "on_hold";

  // Active states.
  if (l.includes("in progress") || l.includes("progress")) return "in_progress";
  if (l === "open" || l.includes("new")) return "open";

  return "other";
}

export function isHealthBucket(s: string | null | undefined): s is HealthBucket {
  return (
    s === "open" ||
    s === "in_progress" ||
    s === "pending_approval" ||
    s === "waiting" ||
    s === "escalated" ||
    s === "on_hold" ||
    s === "other"
  );
}
